import React, { useEffect, useRef, useMemo } from 'react';

/**
 * RankingLeaderboard
 * 
 * Exibe a lista do ranking num formato simples e focado no texto.
 */
const RankingLeaderboard = ({ leaderboard = [], currentMemberId, lastUpdatedAt, loading }) => {
    const memberRowRef = useRef(null);
    const scrollContainerRef = useRef(null);

    // Posição do membro atual
    const currentMemberPosition = useMemo(() => {
        const entry = leaderboard.find(e => e.id === currentMemberId);
        return entry?.position || null;
    }, [leaderboard, currentMemberId]);

    // Auto-scroll até o membro atual quando o leaderboard carrega
    useEffect(() => {
        if (memberRowRef.current && scrollContainerRef.current) {
            const timer = setTimeout(() => {
                memberRowRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            }, 600);
            return () => clearTimeout(timer);
        }
    }, [leaderboard, currentMemberId]);

    if (loading) {
        return (
            <div className="d-flex justify-content-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Carregando...</span>
                </div>
            </div>
        );
    }

    if (!leaderboard || leaderboard.length === 0) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center text-muted text-center p-5" style={{ minHeight: "200px" }}>
                <i className="mdi mdi-trophy-outline mb-3" style={{ fontSize: "48px", opacity: 0.4 }}></i>
                <h5 className="fw-bold text-dark">Ranking ainda não disponível</h5>
                <p className="mb-0">O ranking será atualizado em breve. Continue treinando!</p>
            </div>
        );
    }

    const formatDate = (ts) => {
        if (!ts) return '';
        try {
            const date = ts.toDate ? ts.toDate() : new Date(ts);
            return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
        } catch {
            return '';
        }
    };

    return (
        <div className="p-3">
            {/* Cabeçalho */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h3 className="mb-0 fw-bold text-dark">Ranking Geral</h3>
                    {lastUpdatedAt && (
                        <p className="text-muted mb-0 font-size-16">
                            <i className="mdi mdi-calendar-blank me-1"></i>
                            Atualizado em {formatDate(lastUpdatedAt)}
                        </p>
                    )}
                </div>
                {currentMemberPosition && (
                    <div className="text-end">
                        <span className="badge rounded-pill px-3 py-2 bg-dark text-white shadow-sm">
                            Sua posição: #{currentMemberPosition} de {leaderboard.length}
                        </span>
                    </div>
                )}
            </div>

            {/* Lista do Ranking */}
            <div
                ref={scrollContainerRef}
                style={{
                    maxHeight: 'calc(100vh - 340px)',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    paddingBottom: '20px',
                    scrollBehavior: 'smooth',
                }}
            >
                {leaderboard.map((entry) => {
                    const isCurrentMember = entry.id === currentMemberId;

                    return (
                        <div
                            key={entry.id}
                            ref={isCurrentMember ? memberRowRef : null}
                            className="d-flex align-items-center mb-3"
                            style={{
                                padding: '16px 12px',
                                borderBottom: '1px solid #f1f1f1',
                                backgroundColor: isCurrentMember ? 'rgba(255, 235, 59, 0.1)' : 'transparent',
                                borderRadius: isCurrentMember ? '8px' : '0px'
                            }}
                        >
                            {/* Posição numeral simples */}
                            <div
                                className="fw-bold text-muted flex-shrink-0"
                                style={{ width: '40px', fontSize: '18px' }}
                            >
                                #{entry.position}
                            </div>

                            {/* Avatar Pequeno */}
                            <div
                                className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 overflow-hidden me-3"
                                style={{
                                    width: '40px',
                                    height: '40px',
                                    backgroundColor: '#f1f1f1',
                                }}
                            >
                                {entry.photoUrl ? (
                                    <img
                                        src={entry.photoUrl}
                                        alt={entry.fullName}
                                        className="w-100 h-100"
                                        style={{ objectFit: 'cover' }}
                                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; e.target.nextSibling.classList.add('d-flex'); }}
                                    />
                                ) : null}
                                <span
                                    className="align-items-center justify-content-center w-100 h-100"
                                    style={{
                                        display: entry.photoUrl ? 'none' : 'flex',
                                        color: '#666',
                                    }}
                                >
                                    <i className="mdi mdi-account font-size-20"></i>
                                </span>
                            </div>

                            {/* Nome e Patente */}
                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                <div className="d-flex align-items-center">
                                    <div className="fw-semibold text-dark text-truncate font-size-16" style={{ maxWidth: isCurrentMember ? 'calc(100% - 50px)' : '100%' }}>
                                        {entry.fullName}
                                    </div>
                                    {isCurrentMember && (
                                        <span className="ms-2 badge bg-success rounded-pill font-size-10 flex-shrink-0" style={{ verticalAlign: 'text-top' }}>
                                            Você
                                        </span>
                                    )}
                                </div>
                                {entry.currentRankName && (
                                    <div className="text-muted font-size-13">
                                        <i className="mdi mdi-shield-star-outline me-1"></i>
                                        {entry.currentRankName}
                                    </div>
                                )}
                            </div>

                            {/* XP */}
                            <div className="text-end flex-shrink-0 ms-2">
                                <span className="fw-bold text-dark font-size-16">
                                    {(entry.xpTotal || 0).toLocaleString('pt-BR')} 
                                    <span className="ms-1 fw-normal text-muted font-size-12">XP</span>
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default RankingLeaderboard;
